import { useLayoutEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DEEPLINK_ACTION,
  DEEPLINK_QUERY_PARAM,
  SHARE_CODE_QUERY_PARAM,
  isDeeplinkAction,
  sanitizeShareCode,
} from "@/shared/config/deeplink";
import { setPendingDeeplink, setPendingShareCode } from "@/shared/model/deeplinkStore";

/**
 * 알림톡 딥링크(`?deeplink=exam_open` 등) 전역 캡처 인터셉터.
 *
 * `/main` 은 access=`"authenticated"` 라 미인증 콜드부트에서는 AuthGuard 가
 * `<Navigate to={LOGIN} replace />` 로 쿼리를 떨군다. 이 컴포넌트는 `<BrowserRouter>` 직속
 * sibling(AuthGuard 밖)에 위치해, 리다이렉트 이전에 의도를 저장한다.
 *
 * `useLayoutEffect` 를 쓰는 이유: React Router 의 `<Navigate>` 는 passive `useEffect` 로
 * 리다이렉트를 커밋한다. `useLayoutEffect`(paint 이전, passive effect 이전 실행)로 캡처하면
 * 저장이 AuthGuard 의 리다이렉트 effect 보다 먼저 커밋되어 순서가 보장된다.
 *
 * 알려진 action 만 저장하고(`isDeeplinkAction`), `deeplink` 파라미터만 제거한다(다른 쿼리는
 * OAuth `?code=` 등과 공존할 수 있으므로 보존).
 */
export function DeepLinkCapture() {
  const [searchParams, setSearchParams] = useSearchParams();

  useLayoutEffect(() => {
    const raw = searchParams.get(DEEPLINK_QUERY_PARAM);
    const hasShareCode = searchParams.has(SHARE_CODE_QUERY_PARAM);

    if (!isDeeplinkAction(raw)) {
      // 유효한 딥링크 없이 share_code 만 남은 경우에도 URL 은 정리한다(값은 미사용).
      if (hasShareCode) {
        const next = new URLSearchParams(searchParams);
        next.delete(SHARE_CODE_QUERY_PARAM);
        setSearchParams(next, { replace: true });
      }
      return;
    }

    setPendingDeeplink(raw); // 저장이 URL strip 보다 선행

    // 초대 등록 진입일 때만 공유 코드를 흡수 — 검증 실패 값은 저장하지 않는다
    // (수동 입력 폴백). 비식별 코드지만 값 로깅은 여전히 하지 않는다.
    if (raw === DEEPLINK_ACTION.INVITE_CODE_REGISTER) {
      const code = sanitizeShareCode(searchParams.get(SHARE_CODE_QUERY_PARAM));
      if (code) setPendingShareCode(code);
    }

    const next = new URLSearchParams(searchParams);
    next.delete(DEEPLINK_QUERY_PARAM);
    next.delete(SHARE_CODE_QUERY_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return null;
}

export default DeepLinkCapture;
